import { useInlineEdit } from '@components/v2/form/hooks/useInlineEdit';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { isRachelChatsListQueryKey } from '../../../../../../services/modules/rachelChats/rachelChats.keys';
import {
  useDeleteRachelChat,
  useRenameRachelChat,
} from '../../../../../../services/modules/rachelChats/rachelChats.mutations';
import { abortRachelTurn } from '../../../../../../services/modules/rachelChats/rachelChats.repository';
import {
  getOrCreateRachelChatInstance,
  hasRachelChatInstance,
  removeRachelChatInstance,
} from '../../../../chat/rachelChatInstances';
import { useRachelChatContext } from '../../../../contexts/Rachel.context';
import { useRachelErrorReporter } from '../../../../hooks/useRachelErrorReporter';
import { useRachelStore } from '../../../../store/rachel.store';
import { UseRachelChatHistoryItemParams } from './rachelChatHistoryItem.types';

export const useRachelChatHistoryItem = ({
  chatId,
  title,
  isActive,
}: UseRachelChatHistoryItemParams) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const queryClient = useQueryClient();
  const { startNewConversation } = useRachelChatContext();
  const { reportRachelError } = useRachelErrorReporter();
  const deleteRachelChat = useDeleteRachelChat();
  const renameRachelChat = useRenameRachelChat();
  const isRenamingRef = useRef(false);
  const draftInputRef = useRef<HTMLDivElement | null>(null);
  const isUnread = useRachelStore((state) => Boolean(state.unreadChatIds[chatId]));

  const renameChat = async (nextTitle: string) => {
    const trimmedTitle = nextTitle.trim();

    if (!trimmedTitle || trimmedTitle === title || isRenamingRef.current) {
      return;
    }

    isRenamingRef.current = true;
    try {
      await renameRachelChat.mutateAsync({ chatId, title: trimmedTitle });
    } catch (error) {
      reportRachelError(error);
    } finally {
      isRenamingRef.current = false;
    }
  };

  const {
    isEditing,
    draftValue,
    handleStartEditing,
    handleChangeDraft,
    handleConfirm,
    handleKeyDown,
  } = useInlineEdit<string>({
    value: title ?? '',
    onConfirm: (nextTitle) => void renameChat(nextTitle),
  });

  const confirmRenameRef = useRef(handleConfirm);

  useEffect(() => {
    confirmRenameRef.current = handleConfirm;
  });

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const isInsideDraftInput = (target: EventTarget | null) =>
      Boolean(draftInputRef.current?.contains(target as Node));

    const keepDraftFocused = (event: MouseEvent) => {
      if (isInsideDraftInput(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    const confirmOnOutsideClick = (event: MouseEvent) => {
      if (isInsideDraftInput(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      confirmRenameRef.current();
    };

    document.addEventListener('mousedown', keepDraftFocused, true);
    document.addEventListener('click', confirmOnOutsideClick, true);

    return () => {
      document.removeEventListener('mousedown', keepDraftFocused, true);
      document.removeEventListener('click', confirmOnOutsideClick, true);
    };
  }, [isEditing]);

  const handleDeleteClick = () => setIsConfirmOpen(true);

  const handleCancelDelete = () => setIsConfirmOpen(false);

  const stopRunningTurn = async () => {
    if (!hasRachelChatInstance(chatId)) {
      return;
    }
    const instance = getOrCreateRachelChatInstance(chatId);
    await Promise.allSettled([instance.stop(), abortRachelTurn(chatId)]);
  };

  const handleConfirmDelete = async () => {
    try {
      await stopRunningTurn();
      await deleteRachelChat.mutateAsync(chatId);
      removeRachelChatInstance(chatId);
      await queryClient.invalidateQueries({
        predicate: (query) => isRachelChatsListQueryKey(query.queryKey),
      });
      if (isActive) {
        startNewConversation();
      }
      setIsConfirmOpen(false);
    } catch (error) {
      reportRachelError(error);
    }
  };

  return {
    isConfirmOpen,
    isDeleting: deleteRachelChat.isPending,
    isEditing,
    isSelected: isActive && !isEditing,
    isUnread,
    draftTitle: draftValue,
    draftInputRef,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
    handleRenameStart: handleStartEditing,
    handleRenameChange: handleChangeDraft,
    handleRenameConfirm: handleConfirm,
    handleRenameKeyDown: handleKeyDown,
  };
};
